<template>
  <div>
    <div class="flex gap-2">
      <div>
        <slot
          name="main-price"
          :label="label"
          :total-price-to-display="mainPriceToDisplay"
          :loading-pricings="loadingPricings"
          :tooltip-text="tooltipText"
          :type="type"
          :is-giftcard="isGiftcard"
          :hide-tooltip="hideTooltip"
        >
          <div class="main-price">
            <span v-if="label">{{ label }}</span>

            <span
              class="h-[30.8px]"
              :class="{
                'w-[60px] bg-gray-200 animate-pulse': !mainPriceToDisplay,
              }"
            >
              <template v-if="mainPriceToDisplay">
                <span>{{ mainPriceToDisplay }}</span>
              </template>
            </span>

            <div v-if="!isGiftcard && !hideTooltip" class="flex">
              <EventBuyBoxPriceBadge :badge-props="badgeProps" />
            </div>
          </div>
        </slot>

        <div class="special-price-label">
          <template v-if="!isGiftcard && !eventGetters.isBoxEvent(kftEvent)">
            <span v-if="specialPriceLabel">{{ specialPriceLabel }}</span>
            <EventBuyBoxPriceSubtitle
              v-else-if="subtitleProps"
              :subtitle-props="subtitleProps"
            />
          </template>
        </div>
      </div>
      <span
        v-if="eventGetters.isBoxEvent(kftEvent) && !isGiftcard"
        class="text-gray-400 text-xs pt-2"
      >
        {{ t('product.show.checkout.shipping.excludingShipping') }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  EventDate,
  EventDateType,
  EventDescription,
  Pricings,
} from '~/composables/types/event'
import { useEventBuyBoxPrice } from '~/composables/eventBuyBoxPrice'
import { eventGetters } from '~/utils/eventGetters'

const props = defineProps({
  label: {
    type: String,
    required: false,
    default: '',
  },
  specialPriceLabel: {
    type: String,
    required: false,
    default: '',
  },
  selectedTime: {
    type: Object as PropType<EventDate | undefined>,
    required: false,
    default: undefined,
  },
  kftEvent: {
    type: Object as PropType<EventDescription | undefined>,
    required: true,
  },
  type: {
    type: String as PropType<EventDateType>,
    required: true,
    default: 'PUBLIC',
  },
  hideTooltip: {
    type: Boolean,
    default: false,
  },
  selectedTicketQuantity: {
    type: Number as PropType<number | null>,
    default: 1,
  },
  hasAvailableDates: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  isGiftcard: {
    type: Boolean,
    default: false,
  },
  loadingPricings: {
    type: Boolean,
    default: false,
  },
  showFromPrefix: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()

const pricingsRef =
  inject<Ref<Record<string, Pricings>>>('pricingsData') ??
  ref<Record<string, Pricings>>({})
const dateId = computed(() => toRef(props, 'selectedTime')?.value?.id)

const { mainPriceToDisplay, subtitleProps, badgeProps, tooltipText } =
  useEventBuyBoxPrice(pricingsRef, {
    dateId,
    quantity: computed(() => props.selectedTicketQuantity ?? 1),
    event: toRef(props, 'kftEvent'),
    isGiftcard: toRef(props, 'isGiftcard'),
    type: toRef(props, 'type'),
    loadingPricings: toRef(props, 'loadingPricings'),
  })
</script>

<style lang="postcss" scoped>
.main-price {
  @apply text-h5 text-gray-900 font-semibold text-nowrap flex gap-2 items-center;
  @apply min-h-[28px];
}

.special-price-label {
  @apply text-label text-gray-500 leading-none flex gap-2;
}
</style>
